import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getProducts, Product } from '../api/products'; // Adjust path if needed
import { getImageUrl } from '../utils/image';

// --- Data & Helpers ---

const MOROCCAN_NAMES = [
    "أمين", "ياسين", "حمزة", "أنس", "سفيان", "رشيد", "نور الدين", "أيوب", "عثمان", "زكرياء",
    "كريم", "بدر", "أشرف", "مهدي", "آدم", "إلياس", "مروان", "عبد الصمد", "جلال", "أيمن"
];

const MOROCCAN_CITIES = [
    "مراكش", "الدار البيضاء", "الرباط", "طنجة", "فاس", "أكادير", "مكناس", "تطوان", "وجدة",
    "القنيطرة", "آسفي", "بني ملال", "خريبكة", "الناظور", "العيون", "المحمدية", "سلا", "تطوان",
    "زاكورة", "الجديدة"
];

interface NotificationItem {
    id: string; // Unique ID for key
    product: Product;
    customerName: string;
    city: string;
    timeAgo: string;
    variantName?: string;
    qty: number;
}

const LivePurchaseNotifications: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [notification, setNotification] = useState<NotificationItem | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const isRTL = i18n.dir() === 'rtl';

    // Fetch products on mount
    useEffect(() => {
        const fetchStoreProducts = async () => {
            try {
                const data = await getProducts();
                if (data && data.length > 0) {
                    setProducts(data);
                }
            } catch (error) {
                console.error("Failed to load products for notifications", error);
            }
        };
        fetchStoreProducts();
    }, []);

    // Logic: Show one -> Wait -> Hide -> Wait -> Repeat
    useEffect(() => {
        if (products.length === 0) return;

        let timer: NodeJS.Timeout;

        const cycle = () => {
            // 1. Create Data
            const randomProduct = products[Math.floor(Math.random() * products.length)];
            const randomName = MOROCCAN_NAMES[Math.floor(Math.random() * MOROCCAN_NAMES.length)];
            const randomCity = MOROCCAN_CITIES[Math.floor(Math.random() * MOROCCAN_CITIES.length)];

            const newNotification: NotificationItem = {
                id: Date.now().toString() + Math.random().toString(),
                product: randomProduct,
                customerName: randomName,
                city: randomCity,
                timeAgo: `${Math.floor(Math.random() * 59) + 1} ${t('common.min_ago', 'min ago')}`,
                qty: 1,
            };

            // 2. Show Notification
            setNotification(newNotification);

            // 3. Schedule Hide (Display Duration: 8s)
            timer = setTimeout(() => {
                setNotification(null); // Hide

                // 4. Schedule Next Show (Delay between: 20s - 40s)
                const nextDelay = Math.random() * (40000 - 20000) + 20000;
                timer = setTimeout(cycle, nextDelay);

            }, 8000);
        };

        // Initial Start Delay (5s)
        timer = setTimeout(cycle, 5000);

        return () => clearTimeout(timer);
    }, [products, t]);

    const [isChatOpen, setIsChatOpen] = useState(false);

    useEffect(() => {
        const handleChatStateChange = (e: Event) => {
            const customEvent = e as CustomEvent;
            setIsChatOpen(customEvent.detail.isOpen);
        };

        window.addEventListener('chatWidgetStateChange', handleChatStateChange);
        return () => window.removeEventListener('chatWidgetStateChange', handleChatStateChange);
    }, []);

    // Don't show if simple notification logic says no, or if logic says yes but chat is open on mobile
    if (!notification && !notification) return null;

    // Positioning
    const positionClass = isRTL ? 'left-auto right-4' : 'right-auto left-4';

    return (
        <div className={`fixed bottom-4 ${positionClass} z-50 flex flex-col gap-3 w-80 pointer-events-none ${isChatOpen ? 'hidden md:flex' : ''}`}>
            <AnimatePresence mode='wait'>
                {notification && (
                    <motion.div
                        key={notification.id}
                        layout
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        onClick={() => navigate(`/product/${notification.product.slug}`)}
                        className="pointer-events-auto bg-white/95 backdrop-blur-sm dark:bg-neutral-800/95 border border-neutral-100 dark:border-neutral-700 shadow-xl rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:shadow-2xl hover:scale-[1.02] transition-all group"
                    >
                        {/* Product Image */}
                        <div className="relative shrink-0">
                            <div className="w-14 h-14 rounded-full overflow-hidden border border-neutral-100 dark:border-neutral-600">
                                <img
                                    src={getImageUrl(notification.product.images[0])}
                                    alt={notification.product.title_fr}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <span className="absolute -bottom-1 -right-1 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                                ✓
                            </span>
                        </div>

                        {/* Content */}
                        <div className="flex flex-col flex-grow min-w-0">
                            {/* Header: Name & City */}
                            <div className="flex justify-between items-baseline mb-0.5">
                                <span className="text-xs font-bold text-neutral-800 dark:text-neutral-100 truncate max-w-[120px]">
                                    {notification.customerName}
                                </span>
                                <span className="text-[10px] text-neutral-500 dark:text-white">
                                    {isRTL ? 'من' : 'from'} {notification.city}
                                </span>
                            </div>

                            {/* Product Title */}
                            <h4 className="text-xs font-medium text-neutral-600 dark:text-neutral-300 line-clamp-1 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                {isRTL ? notification.product.title_ar : notification.product.title_fr}
                            </h4>

                            {/* Footer: Time ago & Label */}
                            <div className="flex items-center justify-between text-[10px] text-neutral-500 dark:text-white">
                                <span>{isRTL ? 'اشترى الآن' : 'Purchase verification'}</span>
                                <span className="font-mono">{isRTL ? 'منذ 2د' : '2m ago'}</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LivePurchaseNotifications;
